import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, verifyOTP, resetAuthUiState } from '../../store/slices/authSlice';
import logo from '../../assets/logo.png';
import hipaa from '../../assets/VitaRPM-HIPAA.png';
import './LoginPage.css';

// If you prefer, set REACT_APP_API_BASE in your .env (e.g. REACT_APP_API_BASE=http://localhost:3000)
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    remember: false,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Read cookies on mount to pre-fill username & remember checkbox
  useEffect(() => {
    dispatch(resetAuthUiState());
    const getCookie = (name) => {
      const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
      return match ? decodeURIComponent(match.split('=')[1]) : null;
    };

    const rememberCookie = getCookie('remember');
    const usernameCookie = getCookie('username');

    if (rememberCookie === 'on' && usernameCookie) {
      setFormData(prev => ({ ...prev, username: usernameCookie, remember: true }));
    } else if (usernameCookie) {
      // If username cookie exists but remember is not set, still pre-fill username
      setFormData(prev => ({ ...prev, username: usernameCookie }));
    }
  }, [dispatch]);

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (localError && (name === 'username' || name === 'password') && value.length > 0) {
      setLocalError('');
    }
  };

  // Handle OTP change
  const handleOtpChange = (e) => {
    setOtp(e.target.value);
    if (localError && e.target.value.length > 0) {
      setLocalError('');
    }
  };

  // Helper to safely parse JSON (returns object or { message: rawText })
  const safeParse = async (res) => {
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      return { message: text };
    }
  };

  // Handle login submission
  const handleLogin = async () => {
    if (!formData.username && !formData.password) {
      setLocalError('Username and password are required.');
      return;
    }
    if (!formData.username) {
      setLocalError('Please enter username.');
      return;
    }
    if (!formData.password) {
      setLocalError('Please enter password.');
      return;
    }

    const resultAction = await dispatch(login({
      username: formData.username,
      password: formData.password,
      remember: formData.remember
    }));

    if (login.fulfilled.match(resultAction)) {
      const data = resultAction.payload;
      if (data.requires_otp) {
        setShowOtp(true);
        setLocalError('');
      } else {
        // Smooth internal navigation
        const targetUser = data.user;
        const redirectUrl = targetUser?.role_id === 3 && targetUser?.practice_id
          ? `/practices/${targetUser.practice_id}`
          : (data.redirectUrl || '/practices');
        navigate(redirectUrl, { replace: true });
      }
    } else {
      setLocalError(resultAction.payload || 'Login failed');
    }
  };

  // Handle OTP verification
  const handleVerifyOtp = async () => {
    if (!otp) {
      setLocalError("Please enter the OTP.");
      return;
    }

    const resultAction = await dispatch(verifyOTP({ otp }));

    if (verifyOTP.fulfilled.match(resultAction)) {
      const data = resultAction.payload;
      const targetUser = data.user;
      const redirectUrl = targetUser?.role_id === 3 && targetUser?.practice_id
        ? `/practices/${targetUser.practice_id}`
        : (data.redirectUrl || '/practices');
      navigate(redirectUrl, { replace: true });
    } else {
      setLocalError(resultAction.payload || 'OTP verification failed');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setLocalError('');

    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (showOtp) {
        await handleVerifyOtp();
      } else {
        await handleLogin();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="vrpm-login-page">
      {/* LEFT PANEL */}
      <div className="vrpm-login-left">

        <div className="vrpm-logo">
          <img src={logo} alt="VitaRPM" />
        </div>

        <div className="vrpm-form-wrapper">

          <h1>Sign in to experience connected care</h1>

          <p className="vrpm-subtitle">
            Access your secure Remote Patient Monitoring workspace to review patient vitals,
            track clinical outcomes, coordinate care, and improve patient engagement.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
            }}
            onKeyDown={handleKeyDown}
            noValidate
          >

            {!showOtp ? (
              <>

                <div className="vrpm-field">

                  <label>Email</label>

                  <input
                    id="username"
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    autoComplete="username"
                  />

                </div>

                <div className="vrpm-field">

                  <label>Password</label>

                  <div className="vrpm-password-wrapper">

                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="current-password"
                    />

                  </div>

                </div>

                <div className="vrpm-links">

                  <div></div>

                  <button
                    type="button"
                    className="vrpm-forgot-btn"
                    onClick={() =>
                      navigate("/forgot-password")
                    }
                  >
                    Forgot Password?
                  </button>

                </div>

              </>
            ) : (

              <div className="vrpm-field">

                <label>OTP</label>

                <input
                  type="text"
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder="Enter OTP"
                  autoFocus
                />

              </div>

            )}

            {(localError || error) && (
              <div className="vrpm-error">
                {localError || error}
              </div>
            )}

            <button
              type="submit"
              className="vrpm-login-btn"
              disabled={isSubmitting}
            >

              {isSubmitting
                ? showOtp
                  ? "Verifying..."
                  : "Signing In..."
                : showOtp
                  ? "Verify OTP"
                  : "Sign In"}

            </button>

          </form>

        </div>

      </div>

      {/* RIGHT PANEL */}

      <div className="vrpm-login-right-container">
        <div className="vrpm-login-right">

          <div className="vrpm-right-overlay">

            <div className="vrpm-hero">

              <h2>
                Revolutionize Patient Care <br></br>with VitaRPM
              </h2>

              <p className="vrpm-quote">
                “With VitaRPM, our clinicians stay connected to patients beyond the clinic.
                The platform is intuitive, secure, and provides the visibility we need to intervene early
                and improve patient outcomes.”
              </p>

              <div className="vrpm-testimonial">

                <div className="vrpm-avatar">

                  <img
                    src="https://randomuser.me/api/portraits/women/71.jpg"
                    alt=""
                  />

                </div>

                <div>

                  <h4>Isabela Torres, MD</h4>

                  <span>
                    Director Digital Health
                  </span>

                </div>

              </div>

            </div>

            <div className="vrpm-bottom">

              <div className="vrpm-divider"></div>
              <div className="vrpm-hipaa">
                <img src={hipaa} alt="VitaRPM" />
              </div>
              <div className="vrpm-cpt-grid">

                <div>CPT 99453</div>
                <div>CPT 99445</div>
                <div>CPT 99454</div>
                <div>CPT 99470</div>
                <div>CPT 99457</div>
                <div>CPT 99458</div>

              </div>

            </div>

          </div>

        </div>
      </div>

    </div>
  );
};

export default LoginPage;
